import { useTranslations } from '@vocab/react';
import {
  Box,
  Checkbox,
  Columns,
  Column,
  Stack,
  Text,
  Notice,
  ContentBlock,
} from 'braid-design-system';
import { useAtomValue } from 'jotai';
import type React from 'react';

import type { Country } from '../../../entities/Country';
import translations from '../../../locales.vocab';
import {
  formValidationErrorsAtom,
  isSaveCardAtom,
} from '../../../usecases/CreditCard/CreditCardStore';
import {
  selectedCardBrandAtom,
  surchargeAtom,
} from '../../../usecases/Surcharges/SurchargeStore';
import type { CreditCardForm } from '../../../usecases/usecaseTypes';
import { AcceptedBrands } from '../../brands/AcceptedBrands';

import StripeSecureCardTip from './StripeSecureCardTip';
import { CardCVCInput } from './formElements/CardCVCInput';
import { CardExpiryInput } from './formElements/CardExpiryInput';
import { CardNumberInput } from './formElements/CardNumberInput';

interface Props {
  creditCardForm?: CreditCardForm;
  country: Country;
}

const StripeForm = ({ creditCardForm, country }: Props) => {
  const { t } = useTranslations(translations);
  const isSaveCard = useAtomValue(isSaveCardAtom);
  const formValidationErrors = useAtomValue(formValidationErrorsAtom);
  const surcharge = useAtomValue(surchargeAtom);
  const selectedCardBrand = useAtomValue(selectedCardBrandAtom);

  if (!creditCardForm) {
    return <></>;
  }

  const { onCreditCardFormChange, showSaveCard, isDisplaySurcharge } =
    creditCardForm;

  return (
    <Box padding="none">
      <Stack space="medium">
        <AcceptedBrands highlightBrand={selectedCardBrand} country={country} />
        <Stack space="medium">
          <ContentBlock width="xsmall" align="left">
            <Stack space="medium">
              <CardNumberInput
                label={t('stripe.form.cardnumber')}
                onInputChange={onCreditCardFormChange}
                formValidationErrors={formValidationErrors}
              />

              <Columns space="medium" collapseBelow="tablet">
                <Column>
                  <CardExpiryInput
                    label={t('stripe.form.cardexpiry')}
                    onInputChange={onCreditCardFormChange}
                    formValidationErrors={formValidationErrors}
                  />
                </Column>
                <Column>
                  <CardCVCInput
                    label={t('stripe.form.cardcvc')}
                    onInputChange={onCreditCardFormChange}
                    formValidationErrors={formValidationErrors}
                  />
                </Column>
              </Columns>
            </Stack>
          </ContentBlock>

          {showSaveCard && ( //  to determine if we want to show save card
            <Checkbox
              id="id"
              checked={isSaveCard!} // this will appear for caja
              onChange={(event: React.BaseSyntheticEvent) => {
                onCreditCardFormChange(event, 'card-is-save');
              }}
              label={t('stripe.form.issave')}
            />
          )}
        </Stack>
        {isDisplaySurcharge && surcharge > 0 && (
          <Notice tone="info">
            <Text>
              {t('a xx% amex surcharge...', {
                surcharge: surcharge.toString(),
              })}
            </Text>
          </Notice>
        )}
        <StripeSecureCardTip />
      </Stack>
    </Box>
  );
};

export default StripeForm;
